/**
 * Story 7.3 Task 1: App Loading Skeleton
 * Grayscale skeleton UI shown before React hydrates
 * Matches SentenceTemplate layout to prevent CLS
 *
 * Story 7.3 Task 3: Added slow network indicator support
 */
import React, { useEffect, useState } from 'react';
import '../styles/skeleton.css';

interface AppSkeletonProps {
  /** Show slow network loading indicator (default: auto-detect after 2s) */
  showSlowIndicator?: boolean;
}

export const AppSkeleton: React.FC<AppSkeletonProps> = ({ showSlowIndicator }) => {
  // Story 7.3 Task 3: Auto-detect slow loading after 2 seconds
  const [isSlowLoading, setIsSlowLoading] = useState(false);

  useEffect(() => {
    // If prop is provided, use it directly
    if (showSlowIndicator !== undefined) {
      setIsSlowLoading(showSlowIndicator);
      return;
    }

    // Auto-detect: Show indicator after 2 seconds
    const timeoutId = setTimeout(() => {
      setIsSlowLoading(true);
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [showSlowIndicator]);

  return (
    <div className="app-skeleton" data-testid="app-skeleton" aria-label="Loading application" role="status">
      {/* Skeleton heading matches .main-heading dimensions */}
      <div className="skeleton-heading" aria-hidden="true" />

      {/* Skeleton fields match SentenceTemplate input fields */}
      <div className="skeleton-field" aria-hidden="true" />
      <div className="skeleton-field" aria-hidden="true" />
      <div className="skeleton-field" aria-hidden="true" />

      {/* Skeleton buttons match button-row layout */}
      <div className="skeleton-buttons" aria-hidden="true">
        <div className="skeleton-button" />
        <div className="skeleton-button" />
      </div>

      {/* Story 7.3 Task 3: Slow network loading indicator */}
      {isSlowLoading && (
        <div className="skeleton-slow-indicator" aria-live="polite">
          <span className="skeleton-loading-text">Loading game...</span>
        </div>
      )}

      {/* Screen reader announcement */}
      <span className="sr-only">
        {isSlowLoading ? 'Loading game, please wait...' : 'Loading, please wait...'}
      </span>
    </div>
  );
};

export default AppSkeleton;
